import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { workOrderService } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { staffService } from '../../services/staffService';
import { FiSave, FiX } from 'react-icons/fi';
import { supabase } from '../../services/supabaseClient';

// Import komponen form
import FormInformasiDasar from './components/FormInformasiDasar';
import FormMaterial from './components/FormMaterial';
import FormTeam from './components/FormTeam';
import FormFabrikasi from './components/FormFabrikasi';
import FormFileUpload from './components/FormFileUpload';
import FormApproval from './components/FormApproval';

const WorkOrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // STATE
  const [formData, setFormData] = useState({
    customerId: '',
    staffId: '',
    category: 'Fabrikasi',
    projectName: '',
    projectCode: '',
    categoryItem: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    startDate: '',
    targetDate: '',
    estimatedDays: '',
    files: [],
    materials: [],
    laborCost: 0,
    totalCost: 0,
    team: [],
    estimatedHours: 0,
    actualHours: 0,
    progress: 0,
    fabrikasiDetails: {
      materialType: '',
      materialThickness: '',
      dimensions: '',
      weight: '',
      weldingType: '',
      surfaceTreatment: '',
      quantity: 1,
      unit: 'unit',
      blueprint: null
    },
    approval: {
      method: 'whatsapp',
      status: 'pending',
      date: ''
    }
  });

  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // STATE MATERIAL
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState({ id: '', qty: 1, name: '', price: 0 });

  // STATE TEAM
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedHours, setSelectedHours] = useState(8);
  const [selectedIsLead, setSelectedIsLead] = useState(false);

  // CONSTANTS
  const statusOptions = [
    { value: 'pending', label: 'Order Masuk' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Selesai' },
    { value: 'cancelled', label: 'Dibatalkan' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Rendah' },
    { value: 'medium', label: 'Sedang' },
    { value: 'high', label: 'Tinggi' },
    { value: 'urgent', label: 'Urgent' }
  ];

  // FUNGSI MATERIAL
  const addMaterial = () => {
    if (!selectedMaterial.id) {
      setError('Pilih material dulu');
      return;
    }

    const qty = parseInt(selectedMaterial.qty) || 1;
    if (qty <= 0) {
      setError('Qty harus lebih dari 0');
      return;
    }
    
    const inventory = availableMaterials.find(m => m.id === selectedMaterial.id);
    if (inventory && inventory.stock < qty) {
      setError(`Stok ${inventory.name} tidak cukup! Tersedia: ${inventory.stock}, Dibutuhkan: ${qty}`);
      return;
    }

    const existing = formData.materials.find(m => m.id === selectedMaterial.id);
    if (existing) {
      setError('Material sudah ditambahkan!');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          id: selectedMaterial.id,
          name: selectedMaterial.name,
          qty: qty,
          price: selectedMaterial.price || 0
        }
      ]
    }));

    setSelectedMaterial({ id: '', qty: 1, name: '', price: 0 });
    setError('');
  };

  const removeMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
  };

  // FUNGSI TEAM
  const addTeamMember = () => {
    if (!selectedStaffId) {
      setError('Pilih staff dulu');
      return;
    }

    const exists = formData.team?.find(m => m.id === selectedStaffId);
    if (exists) {
      setError('Staff sudah ada di tim!');
      return;
    }

    const staffMember = staff.find(s => s.id === selectedStaffId);
    if (!staffMember) {
      setError('Staff tidak ditemukan');
      return;
    }

    const newMember = {
      id: staffMember.id,
      name: staffMember.name,
      photo: staffMember.photo || '',
      role: selectedRole || 'Anggota',
      hours: parseInt(selectedHours) || 8,
      rate: parseInt(staffMember.rate) || 0,
      isLead: selectedIsLead
    };

    setFormData(prev => ({
      ...prev,
      team: [...(prev.team || []), newMember],
      estimatedHours: (prev.estimatedHours || 0) + (newMember.hours || 0)
    }));

    setSelectedStaffId('');
    setSelectedRole('');
    setSelectedHours(8);
    setSelectedIsLead(false);
    setError('');
  };

  const removeTeamMember = (index) => {
    const member = formData.team[index];
    setFormData(prev => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index),
      estimatedHours: (prev.estimatedHours || 0) - (member?.hours || 0)
    }));
  };

  // LOAD DATA
  useEffect(() => {
    const init = async () => {
      await loadData();
      await loadInventory();
      if (isEdit) {
        await loadWorkOrder();
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInventory = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('id, name, stock, sellPrice, buyPrice')
        .gt('stock', 0);

      if (error) throw error;
      setAvailableMaterials(data || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
      setError('Gagal memuat data inventory');
    }
  };

  const loadData = async () => {
    try {
      const [customersData, staffData] = await Promise.all([
        customerService.getAll(),
        staffService.getAll()
      ]);
      setCustomers(customersData || []);
      setStaff(staffData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadWorkOrder = async () => {
    try {
      const wo = await workOrderService.getById(id);
      if (wo) {
        setFormData({
          ...wo,
          startDate: wo.startDate ? wo.startDate.split('T')[0] : '',
          targetDate: wo.targetDate ? wo.targetDate.split('T')[0] : '',
          materials: wo.materials || [],
          laborCost: wo.laborCost || 0,
          totalCost: wo.totalCost || 0,
          team: wo.team || [],
          estimatedHours: wo.estimatedHours || 0,
          actualHours: wo.actualHours || 0,
          progress: wo.progress || 0,
          approval: wo.approval || { method: 'whatsapp', status: 'pending', date: '' },
          fabrikasiDetails: {
            materialType: wo.fabrikasiDetails?.materialType || '',
            materialThickness: wo.fabrikasiDetails?.materialThickness || '',
            dimensions: wo.fabrikasiDetails?.dimensions || '',
            weight: wo.fabrikasiDetails?.weight || '',
            weldingType: wo.fabrikasiDetails?.weldingType || '',
            surfaceTreatment: wo.fabrikasiDetails?.surfaceTreatment || '',
            quantity: wo.fabrikasiDetails?.quantity || 1,
            unit: wo.fabrikasiDetails?.unit || 'unit',
            blueprint: wo.fabrikasiDetails?.blueprint || null
          }
        });
      }
    } catch (error) {
      console.error('Error loading work order:', error);
      setError('Gagal memuat data work order');
    }
  };

  // HANDLE CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFabrikasiChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      fabrikasiDetails: {
        ...prev.fabrikasiDetails,
        [name]: value
      }
    }));
  };

  const handleApprovalChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      approval: {
        ...prev.approval,
        [name]: value
      }
    }));
  };

  const handleFileChange = (files) => {
    setFormData(prev => ({
      ...prev,
      files: files
    }));
  };

  const handleFileRemove = (id) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== id)
    }));
  };

  // HANDLE SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    for (const material of formData.materials) {
      const inventory = availableMaterials.find(m => m.id === material.id);
      if (inventory && inventory.stock < material.qty) {
        setError(`Stok ${inventory.name} tidak cukup! Tersedia: ${inventory.stock}, Dibutuhkan: ${material.qty}`);
        setLoading(false);
        return;
      }
    }

    try {
      if (!formData.customerId) {
        setError('Pilih customer terlebih dahulu');
        setLoading(false);
        return;
      }
      if (!formData.staffId) {
        setError('Pilih staff terlebih dahulu');
        setLoading(false);
        return;
      }
      if (!formData.projectName.trim()) {
        setError('Nama pekerjaan wajib diisi');
        setLoading(false);
        return;
      }

      const fabrikasiDetails = formData.fabrikasiDetails || {};
      const { categoryItem, ...formDataWithoutCategoryItem } = formData;

      const materialTotal = formData.materials.reduce((sum, m) => sum + (m.qty * m.price), 0);
      const laborCost = parseInt(formData.laborCost) || 0;

      const dataToSave = {
        ...formDataWithoutCategoryItem,
        categoryItem: formData.categoryItem || '',
        estimatedDays: parseInt(formData.estimatedDays) || 0,
        team: formData.team || [],
        estimatedHours: formData.team?.reduce((sum, m) => sum + (m.hours || 0), 0) || 0,
        fabrikasiDetails: {
          ...fabrikasiDetails,
          materialThickness: parseFloat(fabrikasiDetails.materialThickness) || 0,
          weight: parseFloat(fabrikasiDetails.weight) || 0,
          quantity: parseInt(fabrikasiDetails.quantity) || 1,
          blueprint: formData.fabrikasiDetails.blueprint || null 
        },
        materials: formData.materials || [],
        laborCost: laborCost,
        totalCost: laborCost + materialTotal
      };

      let result;
      if (isEdit) {
        result = await workOrderService.update(id, dataToSave);
      } else {
        result = await workOrderService.create(dataToSave);
      }

      if (result) {
        navigate('/work-orders');
      } else {
        setError('Gagal menyimpan work order');
      }
    } catch (error) {
      console.error('Error saving work order:', error);
      const errorMessage = error?.message || error?.toString() || 'Terjadi kesalahan saat menyimpan';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // RENDER
  const customersArray = Array.isArray(customers) ? customers : [];
  const staffArray = Array.isArray(staff) ? staff : [];

  const materialTotal = formData.materials.reduce((sum, m) => sum + (m.qty * m.price), 0);
  const laborCost = parseInt(formData.laborCost) || 0;
  const grandTotal = laborCost + materialTotal;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {isEdit ? 'Edit Work Order' : 'New Work Order'}
        </h1>
        <button
          onClick={() => navigate('/work-orders')}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <FiX size={20} />
          <span>Cancel</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        
        <FormInformasiDasar
          formData={formData}
          handleChange={handleChange}
          customersArray={customersArray}
          staffArray={staffArray}
          statusOptions={statusOptions}
          priorityOptions={priorityOptions}
        />

        <FormMaterial
          formData={formData}
          handleChange={handleChange}
          availableMaterials={availableMaterials}
          selectedMaterial={selectedMaterial}
          setSelectedMaterial={setSelectedMaterial}
          onAddMaterial={addMaterial}
          onRemoveMaterial={removeMaterial}
          formatCurrency={formatCurrency}
          materialTotal={materialTotal}
          grandTotal={grandTotal}
        />

        <FormTeam
          formData={formData}
          staffArray={staffArray}
          selectedStaffId={selectedStaffId}
          setSelectedStaffId={setSelectedStaffId}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          selectedHours={selectedHours}
          setSelectedHours={setSelectedHours}
          selectedIsLead={selectedIsLead}
          setSelectedIsLead={setSelectedIsLead}
          onAddTeamMember={addTeamMember}
          onRemoveTeamMember={removeTeamMember}
        />

        <FormFabrikasi
          formData={formData}
          handleFabrikasiChange={handleFabrikasiChange}
          setFormData={setFormData}
        />

        <FormFileUpload
          formData={formData}
          onFileChange={handleFileChange}
          onFileRemove={handleFileRemove}
        />

        <FormApproval
          formData={formData}
          handleApprovalChange={handleApprovalChange}
        />

        <div className="flex items-center space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <FiSave size={20} />
            <span>{loading ? 'Saving...' : isEdit ? 'Update' : 'Create'} Work Order</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/work-orders')}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkOrderForm;